import type { LabelInValue, UserList } from 'models';
import { type ComponentProps, useMemo, useState } from 'react';
import { InfinityScrollSelect } from 'ui';
import { useDebounce } from 'common/hooks';
import { useCommunitiesUsersInfiniteQuery } from '../../queries';

export type CommunityUsersSelectProperties<T extends LabelInValue> = {
  communityIds: string[];
  createOption: (value: UserList) => T;
  query?: Partial<Parameters<typeof useCommunitiesUsersInfiniteQuery>[0]>;
} & Omit<ComponentProps<typeof InfinityScrollSelect<T>>, 'handleRequestMoreData' | 'hasMoreData'>;

const CommunityUsersSelect = <T extends LabelInValue>({
  communityIds,
  createOption,
  query,
  ...props
}: CommunityUsersSelectProperties<T>) => {
  const [search, setSearch] = useState<string>();
  const { isDebouncing, value } = useDebounce(search);

  const {
    data: communityUsers,
    fetchNextPage: fetchNextPageOfCommunityUsers,
    hasNextPage: hasMoreCommunityUsers,
    isFetching: isFetchingCommunityUsers,
  } = useCommunitiesUsersInfiniteQuery({
    filter: { communitiesIds: communityIds, search: value, ...query?.filter },
    pagination: { pageSize: 10, ...query?.pagination },
  });

  const options = useMemo(
    () => communityUsers?.pages.flatMap((page) => page.map((user) => createOption(user))) ?? [],
    [createOption, communityUsers],
  );

  return (
    <InfinityScrollSelect<T>
      handleRequestMoreData={fetchNextPageOfCommunityUsers}
      hasMoreData={hasMoreCommunityUsers}
      loading={isFetchingCommunityUsers || isDebouncing}
      onSearch={setSearch}
      options={options}
      {...props}
    />
  );
};

export default CommunityUsersSelect;
